import { useQuery } from '@tanstack/react-query';
import {
  type EmployeeCostResponse,
  type EmployeeResponse,
  formatMoney,
  salaryMonthLabel,
  toCents,
} from '@k-management/shared';
import { useState } from 'react';
import { apiRequest } from '../../lib/api.js';
import { formatHours, peopleKeys } from './people-ui.js';

/**
 * The card of an employee › «Costo aziendale» (2.37): the cost month by month
 * from the payroll loaded, the €/hour of the year and the hours. Only with
 * «persone.costs».
 */
export function EmployeeCost({ employee }: { employee: EmployeeResponse }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const cost = useQuery({
    queryKey: peopleKeys.cost(employee.id, year),
    queryFn: () =>
      apiRequest<EmployeeCostResponse>(`/people/employees/${employee.id}/cost?year=${year}`),
  });
  const data = cost.data;
  const years = [now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2];
  const hoursTotal = data
    ? toCents(data.totals.hours) +
      toCents(data.leave.leaveHours) +
      toCents(data.leave.rolHours) +
      toCents(data.leave.holidayHours) +
      toCents(data.leave.sickHours)
    : 0;

  return (
    <div className="stack">
      <div className="row month-picker">
        <select
          aria-label="Anno"
          value={year}
          onChange={(event) => setYear(Number(event.target.value))}
        >
          {years.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </select>
      </div>
      {cost.isError && <p className="error">{cost.error.message}</p>}
      {data && data.months.length === 0 && (
        <p className="muted">
          Nessuna paga caricata per il {year}: si caricano da Persone › Costo del personale.
        </p>
      )}
      {data && data.months.length > 0 && (
        <>
          <div className="kpi-row">
            <div className="card kpi">
              <small className="muted">Costo {year}</small>
              <strong>{formatMoney(data.totals.total)}</strong>
              <small className="muted">
                {data.thirteenthAccrued ? 'con il rateo della tredicesima' : 'tredicesima compresa'}
              </small>
            </div>
            <div className="card kpi">
              <small className="muted">Ore lavorate</small>
              <strong>{formatHours(data.totals.hours)}</strong>
              <small className="muted">
                su {formatHours((hoursTotal / 100).toFixed(2))} ore pagate
              </small>
            </div>
            <div className="card kpi">
              <small className="muted">€/ora diretto</small>
              <strong>{data.totals.hourly ? formatMoney(data.totals.hourly) : '—'}</strong>
              <small className="muted">
                {data.previousHourly ? `${year - 1}: ${formatMoney(data.previousHourly)}` : ' '}
              </small>
            </div>
            <div className="card kpi">
              <small className="muted">Costo per 1 € di lordo</small>
              <strong>{data.costPerGross ? formatMoney(data.costPerGross) : '—'}</strong>
              <small className="muted">quanto costa all’azienda 1 € di lordo</small>
            </div>
          </div>
          <div className="card stack">
            <h2>Mese per mese</h2>
            <div className="table-scroll">
              <table className="data-table cost-table">
                <thead>
                  <tr>
                    <th>Mese</th>
                    <th className="numeric">Lordo</th>
                    <th className="numeric">INPS az.</th>
                    <th className="numeric">TFR</th>
                    <th className="numeric" title="Metasalute, buoni pasto, welfare">
                      Altri
                    </th>
                    <th className="numeric">Rateo 13ª</th>
                    <th className="numeric">Totale</th>
                    <th className="numeric">Ore</th>
                    <th className="numeric">€/ora</th>
                    <th className="numeric">Ferie</th>
                    <th className="numeric">ROL</th>
                    <th className="numeric">Festività</th>
                    <th className="numeric">Malattia</th>
                  </tr>
                </thead>
                <tbody>
                  {data.months.map((month) => (
                    <tr key={month.month}>
                      <td>{salaryMonthLabel(month.month)}</td>
                      <td className="numeric">{formatMoney(month.gross)}</td>
                      <td className="numeric">{formatMoney(month.inpsCompany)}</td>
                      <td className="numeric">{formatMoney(month.tfr)}</td>
                      <td className="numeric">{formatMoney(month.other)}</td>
                      <td className="numeric">
                        {Number(month.thirteenthAccrual)
                          ? formatMoney(month.thirteenthAccrual)
                          : '—'}
                      </td>
                      <td className="numeric">{formatMoney(month.total)}</td>
                      <td className="numeric">{formatHours(month.hours)}</td>
                      <td className="numeric">{month.hourly ? formatMoney(month.hourly) : '—'}</td>
                      <td className="numeric">{formatHours(month.leaveHours)}</td>
                      <td className="numeric">{formatHours(month.rolHours)}</td>
                      <td className="numeric">{formatHours(month.holidayHours)}</td>
                      <td className="numeric">{formatHours(month.sickHours)}</td>
                    </tr>
                  ))}
                  <tr className="total">
                    <td>Anno</td>
                    <td className="numeric">{formatMoney(data.totals.gross)}</td>
                    <td className="numeric">{formatMoney(data.totals.inpsCompany)}</td>
                    <td className="numeric">{formatMoney(data.totals.tfr)}</td>
                    <td className="numeric">{formatMoney(data.totals.other)}</td>
                    <td className="numeric">
                      {Number(data.totals.thirteenthAccrual)
                        ? formatMoney(data.totals.thirteenthAccrual)
                        : '—'}
                    </td>
                    <td className="numeric">{formatMoney(data.totals.total)}</td>
                    <td className="numeric">{formatHours(data.totals.hours)}</td>
                    <td className="numeric">
                      {data.totals.hourly ? formatMoney(data.totals.hourly) : '—'}
                    </td>
                    <td className="numeric">{formatHours(data.leave.leaveHours)}</td>
                    <td className="numeric">{formatHours(data.leave.rolHours)}</td>
                    <td className="numeric">{formatHours(data.leave.holidayHours)}</td>
                    <td className="numeric">{formatHours(data.leave.sickHours)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="muted small">
              L’€/ora del singolo mese oscilla con ferie e festività: fa fede quello dell’anno.
              «Altri» = Metasalute, buoni pasto e welfare. INAIL e auto aziendale dalla 2.39.
            </p>
          </div>
        </>
      )}
    </div>
  );
}
